import { Messages } from '../../codegen/Localize';
import { ServiceLocator } from '../../app/ServiceLocator';
import { ServerService } from '../../ts/services/ServerService';

/**
 * Sets the tab title to just the server name
 */
function setTitleToServerName() {
  document.title = getServerService().serverName();
}

/**
 * Sets the tab title to a place name returned from a function followed by the server name. EX: 'settings', 'users', etc
 * Recommended use, pass in a string function from Messages
 * Ex: BrowserTitleService.setTitleWithPlaceNameFunction({placeNameMessagesFunction: Messages.Catalog})
 */
function setTitleWithPlaceNameFunction(params: {placeNameFunction: () => string}) {
  setTitleWithTranslatedSpace({translatedSpaceName: params.placeNameFunction()});
}

/**
 * Sets the tab title to an already translated static space name followed by the server name. EX: 'settings', 'users', etc
 */
function setTitleWithTranslatedSpace(params: {translatedSpaceName: string}) {
  const serverName = getServerService().serverName();
  document.title = Messages.browserTitle_place({ PLACE: params.translatedSpaceName, SERVER_NAME: serverName });
}

/**
 * Sets the tab title to be a non-translated place name followed by the server name. EX: <Project Name> - <Server Name>
 */
function setTitleWithPlaceEntityName(params: {placeEntityName: string}) {
  const serverName = getServerService().serverName();
  document.title = Messages.browserTitle_place({ PLACE: params.placeEntityName, SERVER_NAME: serverName });
}

/**
 * Set the tab title to be a non-translated space name followed by the already translated subspace name,
 * followed by the server name. EX: <Project Name> : 'workbooks' - <Server Name>
 */
function setTitleWithSpaceEntityNameAndTranslatedSubspaceName(params: {spaceEntityName: string, translatedSubspaceName: string}) {
  if (params.spaceEntityName) {
    const serverName = getServerService().serverName();
    document.title = Messages.browserTitle_objectPlace({ OBJECT: params.spaceEntityName, PLACE: params.translatedSubspaceName, SERVER_NAME: serverName });
  } else {
    setTitleWithTranslatedSpace({translatedSpaceName: params.translatedSubspaceName});
  }
}

function setVizTitle(viewName: string, workbookName: string) {
  const serverName = getServerService().serverName();
  document.title = Messages.browserTitle_viz({ VIEW: viewName, WORKBOOK: workbookName, SERVER_NAME: serverName });
}

// Delays calling the ServiceLocator until a function is called.
// This helps prevent the ServiceLocator being called before #ng-app is setup.
function getServerService() {
  return ServiceLocator.get<ServerService>('ServerService');
}

export const BrowserTitleService = {
  setTitleToServerName,
  setTitleWithPlaceNameFunction,
  setTitleWithTranslatedSpace,
  setTitleWithPlaceEntityName,
  setTitleWithSpaceEntityNameAndTranslatedSubspaceName,
  setVizTitle,
};

// Easy type definition that is used in some cases.
export type BrowserTitleService = typeof BrowserTitleService;
